import React from 'react';
import { takeEvery, put, call } from 'redux-saga/effects';
import { setCohort } from 'Workspace/Cohorts/store/actions';
import { FormattedMessage } from 'react-intl';
import { pushToast } from 'containers/ToastManager/actions';
import catchErrors from 'utils/commands/catchErrors';
import { isValidProductModel } from 'Workspace/Cohorts/components/CohortsPage/productModelConstants';

import messages from '../messages';
import * as constants from '../constants';

const checkProps = (props) => {
  /* This is could be considered a distructive message, since it overwrites the
   * entire cohort.  therefore, lets test and throw if anything important is missing
   */

  // Test property existence
  // I'm keeping this a very much temporary (and not in the i8l lists)
  // as I anticipate this being moved into the cohort project in the future.
  ['cohortId', 'name', 'enrollmentType', 'startDate']
    .forEach((propName) => {
      const prop = props[propName];
      if (prop === undefined || prop === null) {
        throw new Error(`${propName} is required to update cohort`);
      }
    });

  // Test that dates are in a sane order
  const startDate = Date.parse(props.startDate);
  if (props.endDate) {
    const endDate = Date.parse(props.endDate);
    if (endDate < startDate) {
      throw new Error('endDate must be after startDate');
    }
  }

  if (props.finishedDate) {
    const finishedDate = Date.parse(props.finishedDate);
    if (finishedDate < startDate) {
      throw new Error('finishedDate must be after startDate');
    }
  }
};

/*
 * Filter cohort properties to only those mentioned in the API.
 * https://dev-developers.smartsparrow.com/messages/workspace-cohort-change/
 */
const filterCohortProperties = (cohort) => (
  [
    'cohortId',
    'name',
    'enrollmentType',
    'startDate',
    'endDate',
    'bannerImage',
    'bannerPattern',
    'color',
    'paymentRequired',
    'paymentMethodsAllowed',
    'costCurrencyCode',
    'costAmountInCents',
    'trialDurationSeconds',
    'trialEndEpochSeconds',
    'productId',
    'productModel',
    'ltiConsumerCredential',
    'workspaceId',
  ].reduce((newCohort, propertyName) => {
    const value = cohort[propertyName];

    if (value === undefined) {
      return newCohort;
    }

    if (propertyName === 'productModel' && value !== null && !isValidProductModel(value)) {
      return newCohort;
    }

    return { ...newCohort, [propertyName]: value };
  }, {}));

export function* sendRequest(connection, action) {
  const { cohort } = action;
  checkProps(cohort);

  const {
    response,
    type: responseType,
  } = yield call(connection.fetch.bind(connection), {
    type: constants.RTM_WORKSPACE_COHORT_CHANGE,
    ...filterCohortProperties(cohort),
  });

  if (responseType === constants.RTM_WORKSPACE_COHORT_CHANGE_OK) {
    // TODO: update docs so they are correct
    yield put(setCohort(response.cohort));
  } else {
    yield put(
      pushToast({
        type: 'simple',
        message: <FormattedMessage {...messages.failedWorkspaceCohortUpdate} />,
      })
    );
  }
}

export default function* (connection) {
  yield takeEvery(constants.COMMAND_WORKSPACE_COHORT_CHANGE, catchErrors, sendRequest, connection);
}
